import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";
import { startOfWeek, differenceInWeeks, format, addWeeks } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "@/hooks/use-toast";

const FOCUS_AREAS = [
  { value: "serve", label: "Serve" },
  { value: "volley", label: "Volley" },
  { value: "return", label: "Return" },
  { value: "bandeja", label: "Bandeja" },
  { value: "smash", label: "Smash" },
  { value: "wall_play", label: "Väggspel" },
  { value: "positioning", label: "Positionering" },
  { value: "footwork", label: "Fotarbete" },
];

const THEME_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16",
];

interface ThemeForm {
  label: string;
  start_week: number;
  end_week: number;
  color: string;
}

const emptyThemeForm: ThemeForm = { label: "", start_week: 1, end_week: 1, color: "#3b82f6" };

interface SeasonRecord {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  [key: string]: unknown;
}

interface GroupRecord {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface SessionRecord {
  id: string;
  session_date: string;
  start_time: string | null;
  duration_minutes: number | null;
  status: string;
  focus_areas: string[] | null;
  [key: string]: unknown;
}

interface ThemeRecord {
  id: string;
  label: string;
  start_week: number;
  end_week: number;
  color: string;
  [key: string]: unknown;
}

export default function SeasonTimeline({ initialSeasonId }: { initialSeasonId?: string | null }) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>(initialSeasonId || "");
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [editingTheme, setEditingTheme] = useState<string | null>(null); // "new" or theme id
  const [themeForm, setThemeForm] = useState<ThemeForm>(emptyThemeForm);

  // Fetch seasons
  const { data: seasons = [] } = useQuery({
    queryKey: ["seasons", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("seasons")
        .select("*")
        .eq("organization_id", organization!.id)
        .order("start_date", { ascending: false });
      return (data ?? []) as SeasonRecord[];
    },
    enabled: !!organization?.id,
  });

  const selectedSeason = seasons.find((s) => s.id === selectedSeasonId);

  // Fetch groups for selected season
  const { data: groups = [] } = useQuery({
    queryKey: ["training_groups", selectedSeasonId],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_groups")
        .select("*")
        .eq("season_id", selectedSeasonId)
        .order("name");
      return (data ?? []) as GroupRecord[];
    },
    enabled: !!selectedSeasonId,
  });

  // Fetch sessions for selected group
  const { data: sessions = [] } = useQuery({
    queryKey: ["training_sessions", selectedGroupId],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_sessions")
        .select("*")
        .eq("group_id", selectedGroupId)
        .order("session_date");
      return (data ?? []) as SessionRecord[];
    },
    enabled: !!selectedGroupId,
  });

  // Fetch themes for selected group
  const { data: themes = [] } = useQuery({
    queryKey: ["season_period_themes", selectedGroupId],
    queryFn: async () => {
      const { data } = await supabase
        .from("season_period_themes" as any)
        .select("*")
        .eq("group_id", selectedGroupId)
        .order("start_week");
      return (data ?? []) as ThemeRecord[];
    },
    enabled: !!selectedGroupId,
  });

  // Group sessions by week number
  const { weekMap, totalWeeks } = useMemo(() => {
    if (!selectedSeason) return { weekMap: new Map<number, SessionRecord[]>(), totalWeeks: 0 };
    const seasonStart = startOfWeek(new Date(selectedSeason.start_date), { weekStartsOn: 1 });
    const seasonEnd = new Date(selectedSeason.end_date);
    const total = Math.max(1, differenceInWeeks(seasonEnd, seasonStart) + 1);

    const map = new Map<number, SessionRecord[]>();
    sessions.forEach((s) => {
      const d = new Date(s.session_date);
      const weekNum = Math.max(1, differenceInWeeks(d, seasonStart) + 1);
      if (!map.has(weekNum)) map.set(weekNum, []);
      map.get(weekNum)!.push(s);
    });
    return { weekMap: map, totalWeeks: total };
  }, [sessions, selectedSeason]);

  // Save theme mutation
  const saveThemeMutation = useMutation({
    mutationFn: async () => {
      if (editingTheme === "new") {
        const { error } = await supabase.from("season_period_themes" as any).insert([{
          group_id: selectedGroupId,
          organization_id: organization!.id,
          label: themeForm.label,
          start_week: themeForm.start_week,
          end_week: themeForm.end_week,
          color: themeForm.color,
        }] as Record<string, unknown>[]);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("season_period_themes" as any)
          .update({
            label: themeForm.label,
            start_week: themeForm.start_week,
            end_week: themeForm.end_week,
            color: themeForm.color,
          } as Record<string, unknown>)
          .eq("id", editingTheme!);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["season_period_themes"] });
      setEditingTheme(null);
      setThemeForm(emptyThemeForm);
      toast({ title: editingTheme === "new" ? t("seasons.timeline.themeCreated") : t("seasons.timeline.themeUpdated") });
    },
    onError: () => toast({ title: t("seasons.timeline.couldNotSaveTheme"), variant: "destructive" }),
  });

  const deleteThemeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("season_period_themes" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["season_period_themes"] });
      toast({ title: t("seasons.timeline.themeDeleted") });
    },
  });

  const startEditTheme = (theme: ThemeRecord) => {
    setEditingTheme(theme.id);
    setThemeForm({
      label: theme.label,
      start_week: theme.start_week,
      end_week: theme.end_week,
      color: theme.color,
    });
  };

  const weekOptions = Array.from({ length: totalWeeks }, (_, i) => i + 1);

  const getWeekDateRange = (weekNum: number) => {
    if (!selectedSeason) return "";
    const seasonStart = startOfWeek(new Date(selectedSeason.start_date), { weekStartsOn: 1 });
    const weekStart = addWeeks(seasonStart, weekNum - 1);
    const weekEnd = addWeeks(weekStart, 1);
    return `${format(weekStart, "d MMM", { locale: sv })} – ${format(weekEnd, "d MMM", { locale: sv })}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "bg-green-100 text-green-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-blue-100 text-blue-800";
    }
  };

  // Auto-select first season/group
  if (!selectedSeasonId && seasons.length > 0) {
    setSelectedSeasonId(seasons[0].id);
  }
  if (selectedSeasonId && !selectedGroupId && groups.length > 0) {
    setSelectedGroupId(groups[0].id);
  }

  return (
    <div className="space-y-4">
      {/* Selectors */}
      <div className="flex flex-wrap gap-3">
        <Select value={selectedSeasonId} onValueChange={(v) => { setSelectedSeasonId(v); setSelectedGroupId(""); }}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder={t("seasons.selectSeason")} /></SelectTrigger>
          <SelectContent>
            {seasons.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder={t("seasons.selectGroup")} /></SelectTrigger>
          <SelectContent>
            {groups.map((g) => (
              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedGroupId && (
          <Button variant="outline" onClick={() => { setEditingTheme("new"); setThemeForm(emptyThemeForm); }}>
            <Plus className="h-4 w-4 mr-1" /> {t("seasons.timeline.addTheme")}
          </Button>
        )}
      </div>

      {!selectedGroupId && (
        <p className="text-sm text-muted-foreground">{t("seasons.timeline.selectSeasonAndGroup")}</p>
      )}

      {/* Theme form */}
      {editingTheme && (
        <Card>
          <CardContent className="py-4 px-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>{t("seasons.timeline.theme")} *</Label>
                <Input
                  value={themeForm.label}
                  onChange={(e) => setThemeForm({ ...themeForm, label: e.target.value })}
                  placeholder="T.ex. Grundteknik (serve + return)"
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label>{t("seasons.timeline.fromWeek")}</Label>
                  <Select value={String(themeForm.start_week)} onValueChange={(v) => setThemeForm({ ...themeForm, start_week: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {weekOptions.map((w) => (
                        <SelectItem key={w} value={String(w)}>V{w}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label>{t("seasons.timeline.toWeek")}</Label>
                  <Select value={String(themeForm.end_week)} onValueChange={(v) => setThemeForm({ ...themeForm, end_week: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {weekOptions.map((w) => (
                        <SelectItem key={w} value={String(w)}>V{w}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">{t("seasons.timeline.color")}</Label>
              <div className="flex gap-2">
                {THEME_COLORS.map((c) => (
                  <button
                    key={c}
                    className={`w-7 h-7 rounded-full border-2 ${themeForm.color === c ? "border-foreground" : "border-transparent"}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setThemeForm({ ...themeForm, color: c })}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => saveThemeMutation.mutate()} disabled={!themeForm.label.trim() || saveThemeMutation.isPending}>
                <Save className="h-4 w-4 mr-1" /> {t("seasons.timeline.save")}
              </Button>
              <Button variant="ghost" onClick={() => { setEditingTheme(null); setThemeForm(emptyThemeForm); }}>
                <X className="h-4 w-4 mr-1" /> {t("leagues.cancel")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Theme bands */}
      {selectedGroupId && themes.length > 0 && totalWeeks > 0 && (
        <div className="space-y-1.5">
          {themes.map((theme: ThemeRecord) => {
            const leftPct = ((theme.start_week - 1) / totalWeeks) * 100;
            const widthPct = ((theme.end_week - theme.start_week + 1) / totalWeeks) * 100;
            return (
              <div key={theme.id} className="relative h-8">
                <div
                  className="absolute h-full rounded-md flex items-center justify-between px-2 text-xs font-medium text-white overflow-hidden"
                  style={{ left: `${leftPct}%`, width: `${widthPct}%`, backgroundColor: theme.color, minWidth: "80px" }}
                >
                  <span className="truncate">{theme.label} (V{theme.start_week}–{theme.end_week})</span>
                  <div className="flex gap-0.5 shrink-0 ml-1">
                    <button className="hover:opacity-80" onClick={() => startEditTheme(theme)}>
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button className="hover:opacity-80" onClick={() => deleteThemeMutation.mutate(theme.id)}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Week-by-week timeline */}
      {selectedGroupId && selectedSeason && (
        <div className="space-y-2">
          {Array.from({ length: totalWeeks }, (_, i) => i + 1).map((weekNum) => {
            const weekSessions = weekMap.get(weekNum) || [];
            const activeThemes = themes.filter((t) => weekNum >= t.start_week && weekNum <= t.end_week);

            return (
              <Card key={weekNum} className="overflow-hidden">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-medium text-sm">{t("seasons.timeline.week")} {weekNum}</span>
                    <span className="text-xs text-muted-foreground">{getWeekDateRange(weekNum)}</span>
                    {activeThemes.map((t) => (
                      <Badge key={t.id} className="text-xs text-white" style={{ backgroundColor: t.color }}>
                        {t.label}
                      </Badge>
                    ))}
                  </div>
                  {weekSessions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t("seasons.timeline.noSessionsThisWeek")}</p>
                  ) : (
                    <div className="space-y-1">
                      {weekSessions.map((s) => (
                        <div key={s.id} className="flex items-center gap-2 text-sm">
                          <span>{format(new Date(s.session_date), "EEEE d/M", { locale: sv })}</span>
                          <span className="text-muted-foreground">{s.start_time?.slice(0, 5)}</span>
                          <span className="text-muted-foreground">{s.duration_minutes} min</span>
                          <Badge variant="secondary" className={`text-xs ${getStatusColor(s.status)}`}>
                            {s.status === "completed" ? t("seasons.timeline.completed") : s.status === "cancelled" ? t("seasons.timeline.cancelled") : t("seasons.timeline.scheduled")}
                          </Badge>
                          {(s.focus_areas || []).map((f: string) => (
                            <Badge key={f} variant="outline" className="text-xs">
                              {FOCUS_AREAS.find(a => a.value === f)?.label || f}
                            </Badge>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
